/*
 * The arithmetic behind the passport's value list and its completeness: which
 * properties sit under which category, and which of them hold a value. Shared
 * by the list (ValuesPanel) and the editor that feeds the rail its counts.
 */

/** Above this many properties the value list opens as a preview. */
export const VALUES_PREVIEW_THRESHOLD = 5;

/** Rows the collapsed value list previews. */
export const VALUES_PREVIEW_ROWS = 3;

/** Whether a stored value counts as set: empty strings, lists and maps do not. */
export function isFilled(value) {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
}

/**
 * The property list in the ontology's order, cut into the groups its category
 * rows open: `[{ key, title, items: [{ property, index }] }]`. Properties
 * before the first category form a group with no header.
 */
export function groupProperties(properties) {
    const result = [];
    let group = { key: '__ungrouped', title: null, items: [] };
    (properties ?? []).forEach((property, index) => {
        if (property.type === 'category') {
            if (group.items.length || group.title) result.push(group);
            group = { key: property.id ?? `category-${index}`, title: property.value, items: [] };
            return;
        }
        group.items.push({ property, index });
    });
    if (group.items.length || group.title) result.push(group);
    return result;
}

/**
 * The property ids an end of life archive keeps (`end_of_life.keep` on the
 * core, the event's writes included, E4), or null when it keeps every
 * value: no record, `keep` null, or anything but a list of property ids,
 * which reads as keep everything (fail toward keeping, as the server reads it).
 */
export function endOfLifeKept(endOfLife) {
    const keep = endOfLife?.keep;

    return Array.isArray(keep) && keep.every((id) => typeof id === 'string' && id !== '') ? keep : null;
}

/**
 * The level's properties a passport still has, in order: every one, unless
 * it reached its end of life with a keep list (`endOfLife`, the core's
 * `end_of_life`), whose reads hold only the kept values (E19, E26). Then only
 * the kept properties stay, and a category row whose group is left empty
 * goes with them, so the list, the Missing switch and the counts cover what
 * the passport keeps rather than call every deleted value missing.
 */
export function keptProperties(properties, endOfLife) {
    const list = properties ?? [];
    const keep = endOfLifeKept(endOfLife);

    if (keep === null) return list;

    const kept = new Set(keep);
    const result = [];
    let category = null;

    list.forEach((property) => {
        if (property.type === 'category') {
            category = property;
            return;
        }

        if (!kept.has(property.id)) return;

        if (category !== null) {
            result.push(category);
            category = null;
        }

        result.push(property);
    });

    return result;
}
